/**
 * The `validation` data layer — every server call this feature makes, wrapped in the scaffold's
 * useApiQuery / useApiMutation. Server data lives ONLY in TanStack Query (lab-scoped keys); the one
 * piece of ephemeral UI state is the in-session list of escalade ids opened at sign-off (the server
 * exposes no escalade-list endpoint), which lives in a tiny Zustand store below.
 *
 * Endpoints (server/src/modules/validation/routes.js, all under /api/v1):
 *   GET  /validation/worklist                       cap validation.resultat.read
 *   GET  /validation/escalades?statut=…             cap validation.valeurcritique.acknowledge  (NOVA-051)
 *   POST /validation/signer-lot                     cap validation.resultat.signoff
 *   POST /validation/resultats/:id/signer           cap validation.resultat.signoff
 *   POST /validation/escalades/:id/acquitter        cap validation.valeurcritique.acknowledge
 *   POST /validation/escalades/:id/tentative        cap validation.valeurcritique.acknowledge
 *   POST /validation/escalades/:id/breakglass       cap validation.valeurcritique.acknowledge
 */
import { create } from "zustand";
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  EscaladeCritique,
  EscaladeTentative,
  EscaladeWorklist,
  Id,
  RecallWorklist,
  ResultatTentative,
  SignItem,
  SignOutcome,
  StatutEscalade,
  ValidationBreakGlass,
  WorklistResultat,
} from "../types";

/** The current lab id (null on the local single-install path → qk falls back to "_"). */
function useLabId(): string | number | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

// ── In-session opened-escalade store ───────────────────────────────────────────
/**
 * A best-effort in-session cache of escalade ids opened at sign-off (the `escalade_id` returned when a
 * signed critical opens a loop). NOVA-051 made the durable worklist (`useEscaladeWorklist`, GET
 * /validation/escalades) the authoritative source; this store is now only a same-session convenience that
 * remembers what THIS operator just opened (useful before the next worklist refetch).
 */
export interface OpenedEscaladeRef {
  escaladeId: Id;
  resultatId: Id;
  openedAt: number;
}

interface OpenedEscaladesState {
  refs: OpenedEscaladeRef[];
  remember: (ref: OpenedEscaladeRef) => void;
  clear: () => void;
}

export const useOpenedEscalades = create<OpenedEscaladesState>((set) => ({
  refs: [],
  remember: (ref) =>
    set((s) =>
      s.refs.some((r) => String(r.escaladeId) === String(ref.escaladeId))
        ? s
        : { refs: [ref, ...s.refs] },
    ),
  clear: () => set({ refs: [] }),
}));

// ── Query keys ─────────────────────────────────────────────────────────────────
export function worklistKey(labId: string | number | null) {
  return qk.scoped(labId, "validation", "worklist");
}
export function escaladeKey(labId: string | number | null, id: Id) {
  return qk.scoped(labId, "validation", "escalade", String(id));
}
/** NOVA-051 — the durable escalation worklist key (scoped by the statut filter). */
export function escaladeWorklistKey(labId: string | number | null, statut: StatutEscalade) {
  return qk.scoped(labId, "validation", "escalades", statut);
}

// ── Queries ──────────────────────────────────────────────────────────────────
/** The biologiste worklist — brut/repris results, criticals first (server-ordered). */
export function useWorklist() {
  const labId = useLabId();
  return useApiQuery<WorklistResultat[]>(worklistKey(labId), "/validation/worklist");
}

/**
 * NOVA-051 — the DURABLE critical-value escalation worklist (GET /validation/escalades?statut=…). Replaces
 * the in-session opened-list as the authoritative work-set; defaults to the open `a_acquitter` set.
 */
export function useEscaladeWorklist(statut: StatutEscalade = "a_acquitter") {
  const labId = useLabId();
  return useApiQuery<EscaladeWorklist>(
    escaladeWorklistKey(labId, statut),
    `/validation/escalades?statut=${statut}`,
  );
}

// ── Mutations ──────────────────────────────────────────────────────────────────
/** Batch sign-off (VAL-1) — N results in one atomic tx; refuses the WHOLE batch on un-acked criticals. */
export function useSignBatch() {
  const labId = useLabId();
  return useApiMutation<SignOutcome[], { items: SignItem[] }>(
    (vars) => api.post<SignOutcome[]>("/validation/signer-lot", vars),
    { invalidate: [worklistKey(labId)] },
  );
}

/** Single sign-off — flips one brut/repris result. */
export function useSignOne() {
  const labId = useLabId();
  return useApiMutation<SignOutcome, { id: Id } & Omit<SignItem, "resultat_id">>(
    ({ id, ...body }) => api.post<SignOutcome>(`/validation/resultats/${id}/signer`, body),
    { invalidate: [worklistKey(labId)] },
  );
}

/** Close the D-29 loop on a captured read-back (VAL-2 active attestation). */
export function useAcknowledgeEscalade() {
  const labId = useLabId();
  return useApiMutation<
    EscaladeCritique,
    { id: Id; read_back_text: string; acquitte_par?: string }
  >(({ id, ...body }) => api.post<EscaladeCritique>(`/validation/escalades/${id}/acquitter`, body), {
    // NOVA-051 — refresh BOTH the per-escalade detail and the durable worklist (an ack leaves a_acquitter).
    invalidate: [qk.scoped(labId, "validation", "escalade"), qk.scoped(labId, "validation", "escalades")],
  });
}

/** Log a ladder attempt (push/call) + advance the silence-sweep. */
export function useRecordTentative() {
  const labId = useLabId();
  return useApiMutation<
    EscaladeTentative,
    { id: Id; canal: EscaladeCritique["canal"]; resultat?: ResultatTentative; notification_ref?: string }
  >(({ id, ...body }) => api.post<EscaladeTentative>(`/validation/escalades/${id}/tentative`, body), {
    invalidate: [qk.scoped(labId, "validation", "escalade"), qk.scoped(labId, "validation", "escalades")],
  });
}

/** Duty-to-warn break-glass (VAL-3) — refuses without a justification, critical fact only. */
export function useInvokeBreakGlass() {
  return useApiMutation<
    ValidationBreakGlass,
    { id: Id; justification: string; medecin_ref?: Id }
  >(({ id, ...body }) => api.post<ValidationBreakGlass>(`/validation/escalades/${id}/breakglass`, body));
}

// ── NOVA-028 — the biologiste lot-recall worklist (INV-4 → D-24, derived) ───────
export function recallWorklistKey(labId: string | number | null) {
  return qk.scoped(labId, "validation", "recall-worklist");
}

/** GET /validation/recall-worklist — the derived worklist of released results on recalled/expired lots. */
export function useRecallWorklist() {
  const labId = useLabId();
  return useApiQuery<RecallWorklist>(recallWorklistKey(labId), "/validation/recall-worklist");
}

// ── NOVA-023 — the manager break-glass post-hoc review queue (VAL-3) ────────────
export function breakGlassReviewKey(labId: string | number | null) {
  return qk.scoped(labId, "validation", "breakglass-review");
}

/** GET /validation/breakglass/review — the pending (reviewed=false) duty-to-warn overrides, oldest first. */
export function useBreakGlassReviewQueue() {
  const labId = useLabId();
  return useApiQuery<ValidationBreakGlass[]>(breakGlassReviewKey(labId), "/validation/breakglass/review");
}

/** POST /validation/breakglass/:id/review — close the mandatory post-hoc review (manager-gated). */
export function useReviewBreakGlass() {
  const labId = useLabId();
  return useApiMutation<ValidationBreakGlass, { id: Id; outcome: string; notes?: string }>(
    ({ id, ...body }) => api.post<ValidationBreakGlass>(`/validation/breakglass/${id}/review`, body),
    { invalidate: [breakGlassReviewKey(labId)] },
  );
}
